import createDOMPurify from 'dompurify';

let DOMPurify;

if (typeof window === 'undefined') {
	const { JSDOM } = require('jsdom');
	const window = (new JSDOM('')).window;
	DOMPurify = createDOMPurify(window);
} else {
	DOMPurify = createDOMPurify;
}

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
	// set all elements owning target to target=_blank
	if ('target' in node) {
		node.setAttribute('target', '_blank');
		// prevent https://www.owasp.org/index.php/Reverse_Tabnabbing
		node.setAttribute('rel', 'noopener noreferrer');
	}
});

const nameSpacedEvents = {};

const addEvent = (el, type, handler, options = {}) => {
	const [ev, namespace] = type.split('.');
	nameSpacedEvents[`${ev}_${namespace}`] = handler;

	if (el.attachEvent) {
		el.attachEvent(`on${ev}`, handler);
	} else {
		el.addEventListener(ev, handler, options);
	}
};

const removeEvent = (el, type, handler, options = {}) => {
	const [ev, namespace] = type.split('.');

	if (el.detachEvent) {
		el.detachEvent(`on${ev}`, nameSpacedEvents[`${ev}_${namespace}`] !== undefined ? nameSpacedEvents[`${ev}_${namespace}`] : handler);
	} else {
		el.removeEventListener(ev, nameSpacedEvents[`${ev}_${namespace}`] !== undefined ? nameSpacedEvents[`${ev}_${namespace}`] : handler, options);
	}
};

const width = (el, borderBox = true) => {
	if (!el) return false;
	if (el === window) {
		return window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
	}
	return borderBox ? el.offsetWidth : el.clientWidth;
};

const height = (el, borderBox = true) => {
	if (!el) return false;
	if (el === document) {
		const body = document.body;
		const html = document.documentElement;
		return Math.max(body.scrollHeight, body.offsetHeight, html.clientHeight, html.scrollHeight, html.offsetHeight);
	}
	if (el === window) {
		return window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
	}
	return borderBox ? el.offsetHeight : el.clientHeight;
};

const offset = (el) => {
	if (!el) return { top: 0, left: 0 };
	const rect = el.getBoundingClientRect();
	const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
	const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
	return { top: Math.round(rect.top + scrollTop), left: Math.round(rect.left + scrollLeft) };
};

const hasClass = (el, className) => {
	if (el.classList) {
		return el.classList.contains(className);
	}
	return new RegExp(`\\b ${className}\\b`).test(el.className);
};

const addClass = (el, className) => {
	if (className.constructor !== Array) className = [className];
	for (let i = 0; i < className.length; i += 1) {
		if (el.classList) el.classList.add(className[i]);
		else if (!hasClass(el, className)) el.className += ` ${className[i]}`;
	}
};

const removeClass = (el, className) => {
	if (className === '') {
		el.className = '';
		return;
	}
	if (className.constructor !== Array) className = [className];
	for (let i = 0; i < className.length; i += 1) {
		if (el.classList) el.classList.remove(className[i]);
		else el.className = el.className.replace(new RegExp(`\\b ${className[i]}\\b`, 'g'), '');
	}
};

const scrollTo = (scrollDuration, scrollDestination) => {
	const cosParameter = (window.pageYOffset - scrollDestination) / 2;
	let scrollCount = 0;
	let oldTimestamp = window.performance.now();

	const step = (newTimestamp) => {
		scrollCount += Math.PI / (scrollDuration / (newTimestamp - oldTimestamp));
		if (scrollCount >= Math.PI) return;
		window.scrollTo(0, Math.round(scrollDestination + cosParameter + (cosParameter * Math.cos(scrollCount))));
		oldTimestamp = newTimestamp;
		window.requestAnimationFrame(step);
	};

	window.requestAnimationFrame(step);
};

const existsCookie = (cookieName) => {
	if (typeof document === 'undefined') return null;
	const regexpCheck = new RegExp(`^(?:.*;)?\\s*${cookieName}\\s*=\\s*([^;]+)(?:.*)?$`);
	return document.cookie.indexOf(cookieName) !== -1 && regexpCheck.test(document.cookie);
};

const setCookie = (cookieName, cookieValue = 1, cookieDuration = 7) => {
	if (typeof document === 'undefined') return;
	const expires = `expires=${new Date(Date.now() + cookieDuration * 864e5)}`;
	const value = typeof cookieValue !== 'string' ? JSON.stringify(cookieValue) : cookieValue;
	document.cookie = `${cookieName}=${value}; ${expires}; path=/`;
};

const getCookie = (cookieName, parseJSON = false) => {
	if (!existsCookie(cookieName) || typeof document === 'undefined') return null;
	let cookieValue = null;
	document.cookie.split(';').forEach((cookie) => {
		if (cookie.indexOf(cookieName) !== -1) {
			cookieValue = cookie.trim().split('=').slice(-1)[0].trim();
		}
	});
	if (parseJSON) {
		return JSON.parse(decodeURIComponent(cookieValue));
	}
	return cookieValue;
};

const deleteCookie = (cookieName) => {
	if (typeof document === 'undefined') return;
	document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/`;
};

const getAllCookies = (includeWithPrefix = false) => {
	if (typeof document === 'undefined') return {};
	const cookies = {};
	document.cookie.split(';').forEach((cookie) => {
		const [cookieKey, cookieValue] = cookie.trim().split('=');
		if (includeWithPrefix && cookieKey.indexOf(includeWithPrefix) !== 0) return;
		cookies[cookieKey.trim()] = cookieValue.trim();
	});
	return cookies;
};

const deleteAllCookies = () => {
	if (typeof document === 'undefined') return;
	const cookies = getAllCookies();
	Object.keys(cookies).forEach((cookie) => deleteCookie(cookie));
};

const purifier = (text, config) => (DOMPurify.sanitize(text, config));

export { addEvent, removeEvent, width, height, offset, hasClass, addClass, removeClass, scrollTo, existsCookie, setCookie, getCookie, deleteCookie, getAllCookies, deleteAllCookies, purifier };
